import React from "react";
import {
  Box,
  Typography,
  FormControl,
  TextField,
  MenuItem,
  Select,
  InputAdornment,
} from "@mui/material";
import plusIcon from "../../../assets/forms/plus.svg";

export type VariantList = {
  umc: string;
  variant: string;
  kw: number;
  nm: number;
  cc2: string;
  model_class: string;
  [key: string]: any;
};

export type AutoDetailsProps = {
  autoCurrentMake: string;
  autoSelectedModel: string;
  autoDisplayDisplacement: number | null;
  autoFuelType?: string;
  autoSelectedVariant: string | null;
  autoVehicleYear: number | string;
  autoGearboxType?: string;
  autoGearboxDriveType?: string;
  autoVariants?: VariantList[];
  onVariantChange?: (variant: VariantList) => void;
};

const AutoCompleteFields: React.FC<AutoDetailsProps> = ({
  autoCurrentMake,
  autoSelectedModel,
  autoDisplayDisplacement,
  autoFuelType = "",
  autoSelectedVariant,
  autoVehicleYear,
  autoGearboxType = "",
  autoGearboxDriveType = "",
  autoVariants = [],
  onVariantChange,
}) => {
  const yearNum = Number(autoVehicleYear);
  let filteredVariants = autoVariants.filter(
    (v) => new Date(v.intro_date).getFullYear() === yearNum,
  );

  //The fallback for the to the previous year.
  if (filteredVariants.length === 0) {
    const years = autoVariants.map((v) => new Date(v.intro_date).getFullYear());
    const prevYears = years.filter((y) => y < yearNum);
    if (prevYears.length > 0) {
      const closest = Math.max(...prevYears);
      filteredVariants = autoVariants.filter(
        (v) => new Date(v.intro_date).getFullYear() === closest,
      );
    } else {
      filteredVariants = [...autoVariants];
    }
  }

  const getVariantDisplayName = (
    variant: VariantList,
    all: VariantList[],
  ): string => {
    let label = variant.variant;
    const same = all.filter((v) => v.variant === variant.variant);

    if (same.length > 1) {
      if (same.some((v) => v.nm !== variant.nm)) {
        label += ` (${variant.nm} NM)`;
      }
      if (same.some((v) => v.cc2 !== variant.cc2)) {
        const parts = variant.model_class.split("-");
        const cls = parts[parts.length - 1].trim();
        label += ` – (${cls})`;
      }
    }
    const torque = `${variant.nm} NM`;
    const cls = variant.model_class.split("-").pop()!.trim();
    return `${label} (${torque}) – (${cls})`;
  };

  const renderVariantField = () => {
    if (filteredVariants.length > 1 && onVariantChange) {
      return (
        <Select
          displayEmpty
          IconComponent={(props) => <img src={plusIcon} alt="" {...props} />}
          value={
            filteredVariants.find((v) => v.variant === autoSelectedVariant)
              ?.umc ?? ""
          }
          onChange={(e) => {
            const sel = filteredVariants.find((v) => v.umc === e.target.value);
            if (sel) onVariantChange(sel);
          }}
          sx={{ textAlign: "start", borderRadius: "8px" }}
          renderValue={(umc) => {
            if (!umc) return <span>Select Variant</span>;
            const v = filteredVariants.find((x) => x.umc === umc)!;
            return getVariantDisplayName(v, filteredVariants);
          }}
        >
          <MenuItem disabled value="">
            Select Variant
          </MenuItem>
          {filteredVariants.map((v) => (
            <MenuItem key={v.umc} value={v.umc}>
              {getVariantDisplayName(v, filteredVariants)}
            </MenuItem>
          ))}
        </Select>
      );
    }

    return (
      <TextField
        variant="outlined"
        value={autoSelectedVariant ?? ""}
        disabled
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <img src={plusIcon} alt="plus" />
            </InputAdornment>
          ),

          sx: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderRadius: "8px",
            },
          },
        }}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        paddingTop: "25px",
      }}
    >
      {/* Make Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Make
          </Typography>
          <TextField
            variant="outlined"
            value={autoCurrentMake}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Model Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Model
          </Typography>
          <TextField
            variant="outlined"
            value={autoSelectedModel}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Year Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Year
          </Typography>
          <TextField
            variant="outlined"
            value={autoVehicleYear}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Engine Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Engine
          </Typography>
          <TextField
            variant="outlined"
            value={
              autoDisplayDisplacement && autoFuelType
                ? `${autoDisplayDisplacement} L - ${autoFuelType}`
                : autoDisplayDisplacement
                  ? `${autoDisplayDisplacement} L`
                  : ""
            }
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Gearbox Type Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Gearbox Type
          </Typography>
          <TextField
            variant="outlined"
            value={autoGearboxType}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Gearbox Drive Type Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Gearbox Drive Type
          </Typography>
          <TextField
            variant="outlined"
            value={autoGearboxDriveType}
            disabled
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <img src={plusIcon} alt="plus" />
                </InputAdornment>
              ),
              sx: {
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "8px",
                },
              },
            }}
          />
        </Box>
      </FormControl>

      {/* Variant Field */}
      <FormControl fullWidth>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <Typography
            sx={{
              color: "var(--gray-700, #344054)",
              fontSize: "14px",
              fontWeight: "500",
            }}
          >
            Variant
          </Typography>
          {renderVariantField()}
        </Box>
      </FormControl>
    </Box>
  );
};

export default AutoCompleteFields;
